import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { FileText, TrendingUp, Clock, Loader2, Rocket, UserCheck, Package, AlertTriangle, Target, Flag, CheckCircle, XCircle, BarChart3, Layers, GitBranch, Info } from 'lucide-react';
import { productsApi, dashboardMetricsApi } from '../services/api';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import UpgradeContextModal from '../components/billing/UpgradeContextModal';
import AssignSeatsModal from '../components/billing/AssignSeatsModal';
import { useLimitCheck } from '../hooks/useLimitCheck';
import { useUpgradeReturn } from '../hooks/useUpgradeReturn';
import ScopeSelector from '../components/dashboard/ScopeSelector';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, ComposedChart, Area, Legend, Cell, PieChart, Pie
} from 'recharts';

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { organization, user, refreshAuth } = useAuth();
  const { safeMode, getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Scope state
  const [scope, setScope] = useState('all');
  const [scopeLabel, setScopeLabel] = useState('All Products');
  const [products, setProducts] = useState([]);
  
  // Basic metrics state
  const [metrics, setMetrics] = useState({
    products: 0,
    active_features: 0,
    in_progress: 0,
    completed: 0,
    blocked: 0,
    in_production: 0,
  });
  
  // Goal metrics state
  const [goalMetrics, setGoalMetrics] = useState(null);
  const [releaseMetrics, setReleaseMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  
  const [showExistingAccountModal, setShowExistingAccountModal] = useState(false);

  // Limit check hook
  const { 
    isChecking, 
    showUpgradeModal, 
    currentUsage, 
    currentLimit,
    checkLimit, 
    closeModal 
  } = useLimitCheck();
  
  // Handle upgrade return (for seat assignments)
  const { showAssignSeats, setShowAssignSeats, newSeatsCount, setNewSeatsCount } = useUpgradeReturn();

  // Info modal state
  const [infoModal, setInfoModal] = useState({ open: false, title: '', content: null });
  
  // Unstable goals drill-down modal
  const [unstableGoalsModal, setUnstableGoalsModal] = useState({ open: false, loading: false, goals: [] });
  
  // Fetch unstable goals for drill-down
  const loadUnstableGoals = async () => {
    setUnstableGoalsModal({ open: true, loading: true, goals: [] });
    try {
      const response = await dashboardMetricsApi.getUnstableGoals(scope);
      setUnstableGoalsModal({ open: true, loading: false, goals: response.data.unstable_goals || [] });
    } catch (err) {
      console.error('Failed to load unstable goals:', err);
      setUnstableGoalsModal({ open: true, loading: false, goals: [] });
    }
  };
  
  // Metric info content definitions
  const metricInfoContent = {
    statusBreakdown: {
      title: 'Status Breakdown',
      content: (
        <div className="space-y-3">
          <p>Counts all goals by their current status:</p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <strong>Active:</strong> Goals being actively tracked and measured
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-gray-400" />
              <strong>Draft:</strong> New or in-progress goals not yet activated
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-violet-500" />
              <strong>Achieved:</strong> Goals that have met their targets
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <strong>Abandoned:</strong> Goals that were discontinued
            </li>
          </ul>
        </div>
      )
    },
    featureSupport: {
      title: 'Feature Support',
      content: (
        <div className="space-y-3">
          <p>Measures what percentage of your <strong>active goals and KPIs</strong> have features aligned to support them.</p>
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg space-y-2">
            <p className="text-sm"><strong>Goals Supported:</strong> Active goals where at least one feature is aligned to any of their KPIs.</p>
            <p className="text-sm"><strong>KPIs Supported:</strong> KPIs (from active goals) with at least one feature directly aligned.</p>
          </div>
          <p className="text-sm text-muted-foreground">Only active goals are included in this calculation. To improve this metric, align features to KPIs in the feature detail view.</p>
        </div>
      )
    },
    stability: {
      title: 'Goal Stability',
      content: (
        <div className="space-y-3">
          <p>Measures goal stability over a <strong>rolling 6-month window</strong> for all goals that have ever been activated.</p>
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg space-y-2">
            <p className="text-sm flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <strong>Stable:</strong> Goals never demoted OR demoted more than 6 months ago
            </p>
            <p className="text-sm flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <strong>Unstable:</strong> Goals moved back to Draft within the last 6 months
            </p>
          </div>
          <p className="text-sm text-muted-foreground">This chart includes Active, Achieved, Abandoned, and Draft goals that have been activated at least once. A goal becomes "unstable" when moved from Active back to Draft (outside the grace period). High stability indicates consistent goal planning.</p>
        </div>
      )
    },
    dataEntry: {
      title: 'Data Entry Compliance',
      content: (
        <div className="space-y-3">
          <p>Measures KPI data entry compliance based on each KPI's <strong>measurement interval</strong> (Weekly, Monthly, or Quarterly).</p>
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg space-y-2">
            <p className="text-sm"><strong>How it works:</strong></p>
            <ul className="text-sm space-y-1 list-disc list-inside ml-2">
              <li>Each KPI requires at least one data entry per period</li>
              <li>Periods are counted from the first full period after goal activation</li>
              <li>Only <strong>Active goals</strong> are included in this calculation</li>
            </ul>
          </div>
          <p className="text-sm text-muted-foreground">Click the compliance card to see which KPIs are missing data entries. Set up reminders in Product Settings to notify the Product Owner before data is due.</p>
        </div>
      )
    }
  };
  
  // Non-compliant KPIs drill-down modal
  const [nonCompliantModal, setNonCompliantModal] = useState({ open: false, loading: false, kpis: [] });
  
  // Fetch non-compliant KPIs for drill-down
  const loadNonCompliantKpis = async () => {
    setNonCompliantModal({ open: true, loading: true, kpis: [] });
    try {
      const response = await dashboardMetricsApi.getNonCompliantKpis(scope);
      setNonCompliantModal({ open: true, loading: false, kpis: response.data.non_compliant_kpis || [] });
    } catch (err) {
      console.error('Failed to load non-compliant KPIs:', err);
      setNonCompliantModal({ open: true, loading: false, kpis: [] });
    }
  };

  // Load products list for scope selector
  const loadProducts = useCallback(async () => {
    try {
      const response = await productsApi.list();
      const productsList = response.data.products || response.data || [];
      setProducts(productsList);
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  }, []);

  // Load basic metrics
  const loadMetrics = useCallback(async (productScope) => {
    setLoading(true);
    setError(null);
    try {
      const response = await productsApi.list();
      const productsList = response.data.products || response.data || [];
      setProducts(productsList);
      
      // Filter by scope if needed
      const filteredProducts = productScope === 'all' 
        ? productsList 
        : productsList.filter(p => p.product_id === productScope);
      
      // Calculate basic metrics from products
      let totalFeatures = 0;
      let inProgress = 0;
      let completed = 0;
      let blocked = 0;
      let inProduction = 0;
      
      for (const product of filteredProducts) {
        totalFeatures += product.features_count || 0;
        inProgress += product.in_progress_count || 0;
        completed += product.completed_count || 0;
        blocked += product.blocked_count || 0;
        inProduction += product.in_production_count || 0;
      }
      
      setMetrics({
        products: filteredProducts.length,
        active_features: totalFeatures,
        in_progress: inProgress,
        completed: completed,
        blocked: blocked,
        in_production: inProduction,
      });
    } catch (err) {
      console.error('Failed to load metrics:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load goal and release metrics
  const loadAdvancedMetrics = useCallback(async (productScope) => {
    setMetricsLoading(true);
    try {
      const [goalsRes, releasesRes] = await Promise.all([
        dashboardMetricsApi.getGoalMetrics(productScope),
        dashboardMetricsApi.getReleaseMetrics(productScope)
      ]);
      setGoalMetrics(goalsRes.data);
      setReleaseMetrics(releasesRes.data);
    } catch (err) {
      console.error('Failed to load advanced metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadMetrics(scope);
    loadAdvancedMetrics(scope);
  }, []);

  // Handle scope change
  const handleScopeChange = (newScope, label) => {
    setScope(newScope);
    setScopeLabel(label);
    loadMetrics(newScope);
    loadAdvancedMetrics(newScope);
  };

  // Check for existing account modal trigger
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('existing_account') === 'true') {
      setShowExistingAccountModal(true);
      // Clean up URL
      window.history.replaceState({}, document.title, '/dashboard');
    }
  }, [location]);

  // Chart colors
  const chartColors = {
    primary: '#8b5cf6',
    secondary: '#6366f1',
    success: '#22c55e',
    warning: '#f59e0b',
    danger: '#ef4444',
    muted: '#6b7280',
    accent: '#a78bfa'
  };

  // Stat cards (basic metrics)
  const statCards = [
    { label: 'Products', value: metrics.products, icon: Package },
    { label: 'Active Features', value: metrics.active_features, icon: FileText },
    { label: 'In Progress', value: metrics.in_progress, icon: Clock },
    { label: 'Completed', value: metrics.completed, icon: TrendingUp },
    { label: 'In Production', value: metrics.in_production, icon: Rocket },
  ];

  // Goal status data for pie chart
  const goalStatusData = goalMetrics ? [
    { name: 'Active', value: goalMetrics.status_breakdown.active, color: chartColors.success },
    { name: 'Draft', value: goalMetrics.status_breakdown.draft, color: chartColors.muted },
    { name: 'Achieved', value: goalMetrics.status_breakdown.achieved, color: chartColors.primary },
    { name: 'Abandoned', value: goalMetrics.status_breakdown.abandoned, color: chartColors.danger },
  ].filter(d => d.value > 0) : [];

  // Stability data for pie chart
  // Stability measures: active goals that have NOT been moved to draft outside grace period in rolling 6 months
  const stabilityData = goalMetrics ? [
    { name: 'Stable', value: goalMetrics.stability_breakdown.stable, color: chartColors.success },
    { name: 'Unstable', value: goalMetrics.stability_breakdown.unstable, color: chartColors.danger },
  ].filter(d => d.value > 0) : [];

  if (loading) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className={cn("flex-1 flex items-center justify-center", colors.bgPrimary)}>
            <div className="flex flex-col items-center gap-4">
              <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
              <p className={colors.textSecondary}>Loading dashboard...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Empty state when no products exist
  const hasNoProducts = products.length === 0;

  if (hasNoProducts) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Dashboard" />
          <main className={cn("flex-1 overflow-y-auto p-6", colors.bgPrimary)}>
            <div className="max-w-7xl mx-auto">
              <div className={cn(
                "rounded-xl border-2 border-dashed p-8 md:p-12",
                colors.borderPrimary
              )}>
                <div className="max-w-3xl mx-auto">
                  {/* Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                      <BarChart3 className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h2 className={cn("text-2xl font-bold text-center mb-4", colors.textPrimary)}>
                    Your Command Center Awaits
                  </h2>
                  
                  {/* Problem Statement */}
                  <p className={cn("text-center mb-8 leading-relaxed", colors.textSecondary)}>
                    Once you create your first product, this dashboard becomes your real-time view into 
                    product health, team velocity, and strategic progress - all in one place.
                  </p>
                  
                  {/* Feature Grid */}
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <Package className="w-4 h-4 text-purple-600" />
                        </div>
                        <h3 className={cn("font-semibold", colors.textPrimary)}>Product Overview</h3>
                      </div>
                      <p className={cn("text-sm", colors.textMuted)}>
                        Track products, active features, work in progress, and completed items at a glance across your entire portfolio.
                      </p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <Target className="w-4 h-4 text-purple-600" />
                        </div>
                        <h3 className={cn("font-semibold", colors.textPrimary)}>Goals & KPIs</h3>
                      </div>
                      <p className={cn("text-sm", colors.textMuted)}>
                        Monitor goal status, stability metrics, and data entry compliance. See which objectives are on track or need attention.
                      </p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <Rocket className="w-4 h-4 text-purple-600" />
                        </div>
                        <h3 className={cn("font-semibold", colors.textPrimary)}>Release Analytics</h3>
                      </div>
                      <p className={cn("text-sm", colors.textMuted)}>
                        Visualize release status, effort trends, slippage patterns, and feature depth to optimize your delivery cadence.
                      </p>
                    </div>
                    
                    <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-purple-600" />
                        </div>
                        <h3 className={cn("font-semibold", colors.textPrimary)}>Trend Analysis</h3>
                      </div>
                      <p className={cn("text-sm", colors.textMuted)}>
                        Interactive charts show effort per release trends, helping you identify patterns and improve estimation accuracy.
                      </p>
                    </div>
                  </div>
                  
                  {/* CTA */}
                  <div className="text-center">
                    <Button 
                      onClick={() => navigate('/products/new')}
                      size="lg"
                      className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    >
                      <Package className="w-5 h-5" />
                      Create Your First Product
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header 
          title="Dashboard"
          action={
            <ScopeSelector 
              scope={scope}
              scopeLabel={scopeLabel}
              products={products}
              onScopeChange={handleScopeChange}
            />
          }
        />
        <main className={cn("flex-1 overflow-y-auto p-6", colors.bgPrimary)}>
          <div className="max-w-7xl mx-auto">
            {/* Blocked features alert */}
            {metrics.blocked > 0 && (
              <div className={cn(
                "mb-4 flex items-center gap-3 p-4 rounded-lg",
                "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
              )}>
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <span className="text-sm text-amber-700 dark:text-amber-300">
                  {metrics.blocked} feature{metrics.blocked > 1 ? 's' : ''} blocked across your products
                </span>
              </div>
            )}

            {/* Basic Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="dashboard-stats-row">
              {statCards.map((stat) => {
                const Icon = stat.icon;
                return (
                  <Card key={stat.label} className={cn("p-4 border", colors.bgCard, colors.borderPrimary)} data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={cn("text-xs font-medium", colors.textSecondary)}>{stat.label}</p>
                        <p className={cn("text-xl font-bold mt-1", colors.textPrimary)} data-testid={`stat-value-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>{stat.value}</p>
                      </div>
                      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", colors.bgTertiary)}>
                        <Icon className={cn("w-4 h-4", colors.textAccent)} />
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Goals/KPIs Section */}
            <div className="mb-8" data-testid="goals-kpis-section">
              <h2 className={cn("text-lg font-semibold mb-4 flex items-center gap-2", colors.textPrimary)}>
                <Target className="w-5 h-5" />
                Goals & KPIs
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Goal Status Breakdown */}
                <Card className={cn("p-5 border relative", colors.bgCard, colors.borderPrimary)}>
                  <button
                    onClick={() => setInfoModal({ open: true, ...metricInfoContent.statusBreakdown })}
                    className={cn("absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors", colors.textMuted)}
                    title="Learn more about this metric"
                  >
                    <Info className="w-4 h-4 text-blue-400" />
                  </button>
                  <h3 className={cn("text-sm font-medium mb-3", colors.textSecondary)}>Status Breakdown</h3>
                  {metricsLoading ? (
                    <div className="h-32 flex items-center justify-center">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                    </div>
                  ) : goalMetrics ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className={cn("text-sm flex items-center gap-2", colors.textSecondary)}>
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                          Active
                        </span>
                        <span className={cn("font-semibold", colors.textPrimary)}>{goalMetrics.status_breakdown.active}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={cn("text-sm flex items-center gap-2", colors.textSecondary)}>
                          <div className="w-2 h-2 rounded-full bg-gray-400" />
                          Draft
                        </span>
                        <span className={cn("font-semibold", colors.textPrimary)}>{goalMetrics.status_breakdown.draft}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={cn("text-sm flex items-center gap-2", colors.textSecondary)}>
                          <div className="w-2 h-2 rounded-full bg-violet-500" />
                          Achieved
                        </span>
                        <span className={cn("font-semibold", colors.textPrimary)}>{goalMetrics.status_breakdown.achieved}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={cn("text-sm flex items-center gap-2", colors.textSecondary)}>
                          <div className="w-2 h-2 rounded-full bg-red-500" />
                          Abandoned
                        </span>
                        <span className={cn("font-semibold", colors.textPrimary)}>{goalMetrics.status_breakdown.abandoned}</span>
                      </div>
                    </div>
                  ) : null}
                </Card>

                {/* Feature Support - moved here, after Status Breakdown */}
                <Card className={cn("p-5 border relative", colors.bgCard, colors.borderPrimary)}>
                  <button
                    onClick={() => setInfoModal({ open: true, ...metricInfoContent.featureSupport })}
                    className={cn("absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors", colors.textMuted)}
                    title="Learn more about this metric"
                  >
                    <Info className="w-4 h-4 text-blue-400" />
                  </button>
                  <h3 className={cn("text-sm font-medium mb-3", colors.textSecondary)}>Feature Support</h3>
                  {metricsLoading ? (
                    <div className="h-32 flex items-center justify-center">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                    </div>
                  ) : goalMetrics?.feature_support ? (
                    <div className="flex flex-col justify-center h-32 space-y-3">
                      {/* Goals Support */}
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold", 
                          goalMetrics.feature_support.goals_percent >= 70 ? "text-green-500" :
                          goalMetrics.feature_support.goals_percent >= 40 ? "text-amber-500" : "text-red-500"
                        )}>
                          {goalMetrics.feature_support.goals_percent}%
                        </div>
                        <div className={cn("text-xs", colors.textMuted)}>
                          {goalMetrics.feature_support.goals_supported} / {goalMetrics.feature_support.goals_total} Goals supported
                        </div>
                      </div>
                      {/* KPIs Support */}
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold", 
                          goalMetrics.feature_support.kpis_percent >= 70 ? "text-green-500" :
                          goalMetrics.feature_support.kpis_percent >= 40 ? "text-amber-500" : "text-red-500"
                        )}>
                          {goalMetrics.feature_support.kpis_percent}%
                        </div>
                        <div className={cn("text-xs", colors.textMuted)}>
                          {goalMetrics.feature_support.kpis_supported} / {goalMetrics.feature_support.kpis_total} KPIs supported
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={cn("h-32 flex items-center justify-center text-sm", colors.textMuted)}>
                      No data available
                    </div>
                  )}
                </Card>

                {/* Stability */}
                <Card 
                  className={cn("p-5 border relative cursor-pointer hover:shadow-md transition-shadow", colors.bgCard, colors.borderPrimary)}
                  onClick={loadUnstableGoals}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); setInfoModal({ open: true, ...metricInfoContent.stability }); }}
                    className={cn("absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10", colors.textMuted)}
                    title="Learn more about this metric"
                  >
                    <Info className="w-4 h-4 text-blue-400" />
                  </button>
                  <h3 className={cn("text-sm font-medium mb-3", colors.textSecondary)}>Goal Stability</h3>
                  {metricsLoading ? (
                    <div className="h-32 flex items-center justify-center">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                    </div>
                  ) : goalMetrics && stabilityData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={120}>
                      <PieChart>
                        <Pie
                          data={stabilityData}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={50}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {stabilityData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: safeMode ? '#1f2937' : '#fff',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            color: safeMode ? '#f3f4f6' : '#1f2937'
                          }}
                          itemStyle={{
                            color: safeMode ? '#f3f4f6' : '#1f2937'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className={cn("h-32 flex items-center justify-center text-sm", colors.textMuted)}>
                      No data available
                    </div>
                  )}
                  <p className={cn("text-xs text-center mt-2", colors.textMuted)}>Rolling 6-month window</p>
                  <p className={cn("text-xs text-center", colors.textMuted)}>Click to see unstable goals</p>
                </Card>

                {/* Data Entry Compliance */}
                <Card 
                  className={cn("p-5 border relative cursor-pointer hover:ring-2 hover:ring-blue-400/50 transition-all", colors.bgCard, colors.borderPrimary)}
                  onClick={loadNonCompliantKpis}
                  data-testid="compliance-card"
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); setInfoModal({ open: true, ...metricInfoContent.dataEntry }); }}
                    className={cn("absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors", colors.textMuted)}
                    title="Learn more about this metric"
                  >
                    <Info className="w-4 h-4 text-blue-400" />
                  </button>
                  <h3 className={cn("text-sm font-medium mb-3", colors.textSecondary)}>Data Entry Compliance</h3>
                  {metricsLoading ? (
                    <div className="h-32 flex items-center justify-center">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                    </div>
                  ) : goalMetrics ? (
                    <div className="flex flex-col items-center justify-center h-32">
                      {/* Use interval-based compliance if available, fallback to simple compliance */}
                      {(() => {
                        const intervalCompliance = goalMetrics.kpi_metrics?.interval_compliance;
                        const compliancePct = intervalCompliance?.compliance_pct ?? goalMetrics.kpi_metrics.data_entry_compliance;
                        const totalKpis = intervalCompliance?.total_kpis ?? goalMetrics.kpi_metrics.total_kpis;
                        const compliantCount = intervalCompliance?.compliant_kpis ?? goalMetrics.kpi_metrics.kpis_with_data;
                        const nonCompliantCount = intervalCompliance?.non_compliant_count ?? 0;
                        
                        return (
                          <>
                            <div className={cn("text-4xl font-bold", 
                              compliancePct >= 70 ? "text-green-500" :
                              compliancePct >= 40 ? "text-amber-500" : "text-red-500"
                            )}>
                              {compliancePct}%
                            </div>
                            <div className={cn("text-xs mt-2", colors.textMuted)}>
                              {compliantCount} / {totalKpis} KPIs compliant
                            </div>
                            {nonCompliantCount > 0 && (
                              <div className="text-xs text-red-500 mt-1">
                                {nonCompliantCount} missing data entries
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : null}
                  <p className={cn("text-xs text-center mt-2", colors.textMuted)}>Based on measurement intervals</p>
                  <p className={cn("text-xs text-center", colors.textMuted)}>Click to see non-compliant KPIs</p>
                </Card>
              </div>
            </div>

            {/* Releases Section */}
            <div className="mb-8" data-testid="releases-section">
              <h2 className={cn("text-lg font-semibold mb-4 flex items-center gap-2", colors.textPrimary)}>
                <Rocket className="w-5 h-5" />
                Releases
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Release Status */}
                <Card className={cn("p-5 border", colors.bgCard, colors.borderPrimary)}>
                  <h3 className={cn("text-sm font-medium mb-3", colors.textSecondary)}>Release Status</h3>
                  {metricsLoading ? (
                    <div className="h-24 flex items-center justify-center">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                    </div>
                  ) : releaseMetrics ? (
                    <div className="flex items-center justify-around h-24">
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold text-amber-500")}>
                          {releaseMetrics.summary.planned}
                        </div>
                        <div className={cn("text-xs", colors.textMuted)}>Planned</div>
                      </div>
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold text-green-500")}>
                          {releaseMetrics.summary.completed}
                        </div>
                        <div className={cn("text-xs", colors.textMuted)}>Completed</div>
                      </div>
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold", colors.textPrimary)}>
                          {releaseMetrics.summary.total}
                        </div>
                        <div className={cn("text-xs", colors.textMuted)}>Total</div>
                      </div>
                    </div>
                  ) : null}
                </Card>

                {/* Average Effort */}
                <Card className={cn("p-5 border", colors.bgCard, colors.borderPrimary)}>
                  <h3 className={cn("text-sm font-medium mb-3", colors.textSecondary)}>Avg Effort Trend</h3>
                  {metricsLoading ? (
                    <div className="h-24 flex items-center justify-center">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                    </div>
                  ) : releaseMetrics ? (
                    <div className="flex flex-col items-center justify-center h-24">
                      <div className={cn("text-3xl font-bold", colors.textAccent)}>
                        {releaseMetrics.effort_trend_avg}
                      </div>
                      <div className={cn("text-xs mt-1", colors.textMuted)}>
                        Average effort points per release
                      </div>
                    </div>
                  ) : null}
                </Card>

                {/* Slippage Summary */}
                <Card className={cn("p-5 border", colors.bgCard, colors.borderPrimary)}>
                  <h3 className={cn("text-sm font-medium mb-3", colors.textSecondary)}>Recent Slippage</h3>
                  {metricsLoading ? (
                    <div className="h-24 flex items-center justify-center">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                    </div>
                  ) : releaseMetrics && releaseMetrics.slippage_trend.length > 0 ? (
                    <div className="flex items-center justify-around h-24">
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold text-amber-500")}>
                          {releaseMetrics.slippage_trend[releaseMetrics.slippage_trend.length - 1]?.sprint_slippage || 0}%
                        </div>
                        <div className={cn("text-xs", colors.textMuted)}>Sprint</div>
                      </div>
                      <div className="text-center">
                        <div className={cn("text-2xl font-bold text-red-500")}>
                          {releaseMetrics.slippage_trend[releaseMetrics.slippage_trend.length - 1]?.release_slippage || 0}%
                        </div>
                        <div className={cn("text-xs", colors.textMuted)}>Release</div>
                      </div>
                    </div>
                  ) : (
                    <div className={cn("h-24 flex items-center justify-center text-sm", colors.textMuted)}>
                      No slippage data
                    </div>
                  )}
                </Card>
              </div>

              {/* Release Depth Chart */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className={cn("p-5 border", colors.bgCard, colors.borderPrimary)}>
                  <h3 className={cn("text-sm font-medium mb-4", colors.textSecondary)}>
                    Release Depth (Features per Release)
                  </h3>
                  {metricsLoading ? (
                    <div className="h-64 flex items-center justify-center">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                    </div>
                  ) : releaseMetrics && releaseMetrics.release_depth.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={releaseMetrics.release_depth}>
                        <CartesianGrid strokeDasharray="3 3" stroke={safeMode ? '#374151' : '#e5e7eb'} />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fill: safeMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                          axisLine={{ stroke: safeMode ? '#374151' : '#e5e7eb' }}
                        />
                        <YAxis 
                          tick={{ fill: safeMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                          axisLine={{ stroke: safeMode ? '#374151' : '#e5e7eb' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: safeMode ? '#1f2937' : '#fff',
                            border: '1px solid #374151',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="feature_count" fill={chartColors.primary} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className={cn("h-64 flex items-center justify-center text-sm", colors.textMuted)}>
                      No release data available
                    </div>
                  )}
                </Card>

                {/* Effort per Release with Trend Line */}
                <Card className={cn("p-5 border", colors.bgCard, colors.borderPrimary)}>
                  <h3 className={cn("text-sm font-medium mb-4", colors.textSecondary)}>
                    Avg Effort per Release with Trend
                  </h3>
                  {metricsLoading ? (
                    <div className="h-64 flex items-center justify-center">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textAccent)} />
                    </div>
                  ) : releaseMetrics && releaseMetrics.effort_per_release.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <ComposedChart data={releaseMetrics.effort_per_release.map(r => ({
                        ...r,
                        trend: releaseMetrics.effort_trend_avg
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke={safeMode ? '#374151' : '#e5e7eb'} />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fill: safeMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                          axisLine={{ stroke: safeMode ? '#374151' : '#e5e7eb' }}
                        />
                        <YAxis 
                          tick={{ fill: safeMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                          axisLine={{ stroke: safeMode ? '#374151' : '#e5e7eb' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: safeMode ? '#1f2937' : '#fff',
                            border: '1px solid #374151',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="avg_effort" name="Avg Effort" fill={chartColors.secondary} radius={[4, 4, 0, 0]} />
                        <Line 
                          type="monotone" 
                          dataKey="trend" 
                          name="Trend Line" 
                          stroke={chartColors.warning} 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={false}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className={cn("h-64 flex items-center justify-center text-sm", colors.textMuted)}>
                      No effort data available
                    </div>
                  )}
                </Card>
              </div>
            </div>

            {/* Slippage Trend Chart */}
            {releaseMetrics && releaseMetrics.slippage_trend.length > 0 && (
              <div className="mb-8">
                <Card className={cn("p-5 border", colors.bgCard, colors.borderPrimary)}>
                  <h3 className={cn("text-sm font-medium mb-4", colors.textSecondary)}>
                    Slippage Trend (Sprint vs Release)
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={releaseMetrics.slippage_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke={safeMode ? '#374151' : '#e5e7eb'} />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fill: safeMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: safeMode ? '#374151' : '#e5e7eb' }}
                      />
                      <YAxis 
                        tick={{ fill: safeMode ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                        axisLine={{ stroke: safeMode ? '#374151' : '#e5e7eb' }}
                        unit="%"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: safeMode ? '#1f2937' : '#fff',
                          border: '1px solid #374151',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`${value}%`, '']}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="sprint_slippage" 
                        name="Sprint Slippage" 
                        stroke={chartColors.warning} 
                        strokeWidth={2}
                        dot={{ fill: chartColors.warning }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="release_slippage" 
                        name="Release Slippage" 
                        stroke={chartColors.danger} 
                        strokeWidth={2}
                        dot={{ fill: chartColors.danger }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            )}
          </div>
        </main>
        
        {/* Existing Account Modal */}
        <Dialog open={showExistingAccountModal} onOpenChange={setShowExistingAccountModal}>
          <DialogContent className={cn(colors.bgCard, colors.borderPrimary)}>
            <DialogHeader>
              <DialogTitle className={colors.textPrimary}>Account Already Exists</DialogTitle>
              <DialogDescription className={colors.textSecondary}>
                This email is already registered. Please sign in with your existing account.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => {
                setShowExistingAccountModal(false);
                navigate('/login');
              }}>
                Go to Login
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Upgrade Modal */}
        <UpgradeContextModal
          isOpen={showUpgradeModal}
          onClose={closeModal}
          limitType="products"
          currentUsage={currentUsage}
          currentLimit={currentLimit}
          returnPath="/dashboard"
          onUpgradeComplete={() => {
            closeModal();
            loadMetrics(scope);
          }}
        />
        
        {/* Assign Seats Modal (shown after checkout) */}
        <AssignSeatsModal
          isOpen={showAssignSeats}
          onClose={() => setShowAssignSeats(false)}
          newSeats={newSeatsCount}
          onComplete={() => {
            setShowAssignSeats(false);
            refreshAuth();
          }}
        />
        
        {/* Metric Info Modal */}
        <Dialog open={infoModal.open} onOpenChange={(open) => setInfoModal(prev => ({ ...prev, open }))}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                {infoModal.title}
              </DialogTitle>
            </DialogHeader>
            <div className="py-2">
              {infoModal.content}
            </div>
            <DialogFooter>
              <Button onClick={() => setInfoModal(prev => ({ ...prev, open: false }))}>
                Got it
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Unstable Goals Drill-Down Modal */}
        <Dialog open={unstableGoalsModal.open} onOpenChange={(open) => setUnstableGoalsModal(prev => ({ ...prev, open }))}>
          <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Unstable Goals (Last 6 Months)
              </DialogTitle>
              <DialogDescription>
                Goals that were moved back to Draft status outside the grace period
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto py-4">
              {unstableGoalsModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : unstableGoalsModal.goals.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p className="text-lg font-medium">All goals are stable!</p>
                  <p className="text-sm mt-1">No goals have been moved to draft outside the grace period in the last 6 months.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {unstableGoalsModal.goals.map((goal) => {
                    const entry = goal.instability_log_entry;
                    const changes = entry?.changes || [];
                    const statusChange = changes.find(c => c.type === 'status_change');
                    
                    return (
                      <div 
                        key={goal.goal_id}
                        className={cn("p-5 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20")}
                      >
                        {/* Goal Header: Product + Status + Title */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className={cn("text-xs px-2 py-0.5 rounded font-medium", colors.bgSecondary, colors.textSecondary)}>
                            {goal.product_name}
                          </span>
                          <span className={cn("text-xs px-2 py-0.5 rounded", 
                            goal.status === 'draft' ? "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" :
                            goal.status === 'active' ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" :
                            "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                          )}>
                            {goal.status}
                          </span>
                        </div>
                        <h4 className={cn("font-semibold text-lg mb-4", colors.textPrimary)}>{goal.goal_title}</h4>
                        
                        {/* Instability Log Entry - Matching Stability Log Format */}
                        {entry && (
                          <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgTertiary)}>
                            {/* Header: Badge + Timestamp */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs px-2 py-0.5 rounded font-medium bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                ⚠️ Instability Triggered
                              </span>
                              <span className={cn("text-xs", colors.textMuted)}>
                                {new Date(entry.timestamp).toLocaleString('en-US', { 
                                  month: 'short', day: 'numeric', year: 'numeric', 
                                  hour: 'numeric', minute: '2-digit', hour12: true 
                                })}
                              </span>
                            </div>
                            
                            {/* User name */}
                            <p className={cn("text-sm mt-2 font-medium", colors.textPrimary)}>
                              {entry.user_name}
                            </p>
                            
                            {/* Status Change */}
                            {statusChange && (
                              <div className={cn("text-sm mt-1", colors.textMuted)}>
                                Changed status: <strong>{statusChange.old_value}</strong> → <strong>{statusChange.new_value}</strong>
                              </div>
                            )}
                            
                            {/* Instability reason */}
                            {entry.instability_reason && (
                              <p className={cn("text-sm mt-2", colors.textMuted)}>
                                Reason: {entry.instability_reason}
                              </p>
                            )}
                            
                            {/* Reactivation Info (if reactivated) */}
                            {entry.reactivated_at && (
                              <div className={cn("mt-4 pt-4 border-t", colors.borderPrimary)}>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs px-2 py-0.5 rounded font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                    ✓ Reactivated
                                  </span>
                                  <span className={cn("text-xs", colors.textMuted)}>
                                    {new Date(entry.reactivated_at).toLocaleString('en-US', { 
                                      month: 'short', day: 'numeric', year: 'numeric', 
                                      hour: 'numeric', minute: '2-digit', hour12: true 
                                    })}
                                  </span>
                                </div>
                                
                                <p className={cn("text-sm mt-2 font-medium", colors.textPrimary)}>
                                  {entry.reactivated_by_name}
                                </p>
                                
                                {entry.reactivation_reason && (
                                  <p className={cn("text-sm mt-1", colors.textMuted)}>
                                    {entry.reactivation_reason}
                                  </p>
                                )}
                                
                                {/* Reactivation changelog */}
                                {entry.reactivation_changelog && entry.reactivation_changelog.length > 0 && (
                                  <div className={cn("text-sm mt-3 space-y-1 pl-3 border-l-2 border-green-300 dark:border-green-700")}>
                                    {entry.reactivation_changelog.map((change, changeIdx) => (
                                      <div key={changeIdx} className={colors.textMuted}>
                                        {change.field === 'kpi_added' && (
                                          <span>Added KPI: <strong>{change.kpi_name || change.new_value}</strong></span>
                                        )}
                                        {change.field === 'kpi_removed' && (
                                          <span>Removed KPI: <strong>{change.kpi_name || change.old_value}</strong></span>
                                        )}
                                        {change.field === 'kpi_modified' && (
                                          <span>Modified KPI: <strong>{change.kpi_name}</strong></span>
                                        )}
                                        {change.field === 'title' && (
                                          <span>Title: <strong>{change.old_value}</strong> → <strong>{change.new_value}</strong></span>
                                        )}
                                        {change.field === 'description' && (
                                          <span>Description updated</span>
                                        )}
                                        {change.field === 'target_date' && (
                                          <span>Target date changed</span>
                                        )}
                                        {change.field === 'owner_id' && (
                                          <span>Owner changed</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setUnstableGoalsModal(prev => ({ ...prev, open: false }))}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Non-Compliant KPIs Drill-Down Modal */}
        <Dialog open={nonCompliantModal.open} onOpenChange={(open) => setNonCompliantModal(prev => ({ ...prev, open }))}>
          <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Non-Compliant KPIs
              </DialogTitle>
              <DialogDescription>
                KPIs missing data entries for required periods based on their measurement interval
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto py-4">
              {nonCompliantModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : nonCompliantModal.kpis.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p className="text-lg font-medium">All KPIs are compliant!</p>
                  <p className="text-sm mt-1">All active goals have KPI data entries for their required periods.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {nonCompliantModal.kpis.map((kpi, idx) => (
                    <div 
                      key={`${kpi.goal_id}-${kpi.kpi_id}-${idx}`}
                      className={cn("p-4 rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20")}
                    >
                      {/* Header: Product + Goal */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={cn("text-xs px-2 py-0.5 rounded font-medium", colors.bgSecondary, colors.textSecondary)}>
                          {kpi.product_name}
                        </span>
                        <span className={cn("text-xs", colors.textMuted)}>
                          Goal: {kpi.goal_title}
                        </span>
                      </div>
                      
                      {/* KPI Name */}
                      <h4 className={cn("font-semibold text-base mb-2", colors.textPrimary)}>
                        {kpi.kpi_name}
                      </h4>
                      
                      {/* Details */}
                      <div className="flex items-center gap-4 flex-wrap text-sm">
                        <div className={cn("flex items-center gap-1", colors.textMuted)}>
                          <Clock className="w-4 h-4" />
                          <span className="capitalize">{kpi.measurement_interval}</span>
                        </div>
                        <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                          <AlertTriangle className="w-4 h-4" />
                          <span>{kpi.periods_behind} period{kpi.periods_behind !== 1 ? 's' : ''} behind</span>
                        </div>
                        <div className={cn(colors.textMuted)}>
                          {kpi.periods_with_data} / {kpi.periods_due} periods with data
                        </div>
                      </div>
                      
                      {/* Next Due Date */}
                      {kpi.next_due_date && (
                        <p className={cn("text-xs mt-2", colors.textMuted)}>
                          Next entry due: {new Date(kpi.next_due_date).toLocaleDateString('en-US', { 
                            month: 'short', day: 'numeric', year: 'numeric' 
                          })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => setNonCompliantModal(prev => ({ ...prev, open: false }))}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DashboardPage;
